<template>
  <div>
    <empty-state
      v-if="!restaurantIds"
      icon="mdi-silverware"
      :title="$t('select_restaurant')"
      :subtitle="$t('please_select_restaurant_to_view_data')"
    />

    <obroci-table
      v-else
      :data="data"
      :loading="isLoading"
      :search-query="searchQuery"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import ObrociTable from "@/components/guest/ObrociTable.vue";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "AdminObrociTab",
  mixins: [hasPrinting, hasExcel],
  components: {
    EmptyState,
    ObrociTable,
  },
  props: {
    searchQuery: String,
    dateRange: Array,
    isActiveTab: Boolean,
    selectedRestaurants: Array,
    restaurantIds: String,
  },
  data() {
    return {
      data: [],
      isLoading: false,
    };
  },
  computed: {
    restaurantNames() {
      if (this.selectedRestaurants && this.selectedRestaurants.length > 0) {
        return this.selectedRestaurants.map(r => r.name).join(', ');
      }
      return 'Admin';
    },
    restaurantSummary() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      const total = this.selectedRestaurants.length;
      return `${this.$t("restaurants")} (${total})`;
    },
    restaurantNamesForFooter() {
      if (!this.selectedRestaurants || this.selectedRestaurants.length === 0) {
        return "";
      }
      return this.selectedRestaurants.map((r) => r.name).join(", ");
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("food"), value: "name", sortable: false },
        {
          text: this.$t("quantity"),
          value: "quantity",
          sortable: false,
          align: "center",
        },
      ];
    },
    totalQuantity() {
      return this.data.reduce((sum, item) => sum + item.quantity, 0);
    },
  },
  watch: {
    isPrinting(isPrinting) {
      if (!isPrinting || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.printObrociTable();
    },
    isExcelDownloading(isDownloading) {
      if (!isDownloading || !this.isActiveTab || !this.data || this.data.length === 0) {
        return;
      }

      this.exportObrociExcel();
    },
    dateRange: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
    restaurantIds: {
      handler() {
        if (this.restaurantIds) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    async loadData() {
      if (!this.restaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.restaurantIds,
        };

        const response = await reportService.getMultipleRestaurantGuest(params);
        this.processData(response.data);
      } catch (error) {
        console.error("Error loading obroci data:", error);
        this.data = [];
      } finally {
        this.isLoading = false;
      }
    },
    processData(data) {
      const mealsMap = {};

      Object.keys(data).forEach((shift) => {
        const shiftData = data[shift];

        Object.keys(shiftData).forEach((mealName) => {
          const parentMeal = shiftData[mealName];

          if (mealsMap[mealName]) {
            mealsMap[mealName].quantity += parentMeal.quantity;
          } else {
            mealsMap[mealName] = {
              name: mealName,
              quantity: parentMeal.quantity,
            };
          }
        });
      });

      this.data = Object.values(mealsMap).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    printObrociTable() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
      }));

      tableItems.push({
        num: '',
        name: `<strong>${this.$t('total').toUpperCase()}</strong>`,
        quantity: `<strong>${this.totalQuantity}</strong>`,
      });

      this.printDocument({
        pageTitle: this.$t('meals'),
        title: this.$t('meals'),
        subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
          this.dateRange[1]
        )}<h4 style="text-align: center">${this.restaurantSummary}</h4>`,
        tables: [
          {
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
          }
        ],
        afterTableHtml:
          this.selectedRestaurants && this.selectedRestaurants.length > 0
            ? `<div style="margin-top: 16px; font-size: 12px;"><strong>${this.$t(
                "restaurants"
              )}:</strong> ${this.restaurantNamesForFooter}</div>`
            : "",
      });
    },
    exportObrociExcel() {
      const tableItems = this.data.map((item, index) => ({
        num: index + 1,
        name: this.toLatin(item.name),
        quantity: item.quantity,
      }));

      tableItems.push({
        num: '',
        name: this.$t('total').toUpperCase(),
        quantity: this.totalQuantity,
      });

      this.downloadExcel({
        fileName: `${this.restaurantNames.replace(/,/g, "_") || "admin"}_obroci_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: [
          {
            name: this.$t('meals'),
            title: this.$t('meals'),
            subtitle: `${this.localeDate(this.dateRange[0])} - ${this.localeDate(
              this.dateRange[1]
            )} - ${this.restaurantSummary}`,
            headers: this.headers.map(h => ({ text: h.text, value: h.value, align: h.align })),
            items: tableItems,
            footer:
              this.selectedRestaurants && this.selectedRestaurants.length > 0
                ? `${this.$t("restaurants")}: ${this.restaurantNamesForFooter}`
                : "",
          }
        ],
      });
    },
  },
};
</script>
